import React, {useContext, useState} from "react";
import {Button, Drawer, Dropdown, Layout, Menu, PageHeader, Tag, Tooltip, Typography} from "antd";
import {EllipsisOutlined, LogoutOutlined, MenuOutlined, OrderedListOutlined, PlusOutlined, UserOutlined} from '@ant-design/icons';
import {Link} from "react-router-dom";
import styled from 'styled-components';
import {hideHeader} from "../../router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { MailOutlined, AppstoreOutlined, SettingOutlined, AreaChartOutlined } from '@ant-design/icons';
import {faCalendarAlt, faEdit, faFileChartLine, faUserFriends} from "@fortawesome/pro-light-svg-icons";
import {AuthContext} from "../../context/AuthContext";
import {modelList} from "../../Modeling/registry";
import TreeRender from "../../utils/TreeRender";
import {useGetOnceHttp} from "../../hooks/inithttp.hook";
import {useHistory} from "react-router";
import {useIsMobile} from "../../hooks/breakpoint.hook";
import {tokens} from "../ui/tokens";

const StyledHeader = styled(Layout.Header)`
  padding: 0 16px;
  position: sticky;
  top: 0;
  z-index: 20;

  /* Шапка липкая и полупрозрачная, поэтому контент под ней размывается —
     иначе графики и таблицы «просвечивали» сквозь неё. Для браузеров без
     backdrop-filter фон остаётся почти непрозрачным. */
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid ${tokens.border};

  @supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    background: rgba(255, 255, 255, 0.72);
    -webkit-backdrop-filter: saturate(180%) blur(14px);
    backdrop-filter: saturate(180%) blur(14px);
  }

  @media (min-width: 768px) {
    padding: 0 24px;
  }
`

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 100%;
`

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${tokens.ink};
  font-weight: 600;
  font-size: 15px;
  white-space: nowrap;

  &:hover {
    color: ${tokens.primary};
  }
`

const Mark = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: linear-gradient(135deg, ${tokens.primary}, ${tokens.accent});
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: -0.5px;
`
const StyledMenu = Menu
const MenuItemStyled = Menu.Item
const MobileBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`
const Logo = styled.div`
  float: left;
  margin-right: 40px;
  margin-left: 20px;
`

const {SubMenu} = Menu

const Branch = ({children, groupDef, idx, TreeChild, key, subMenuKey, eventKey, ...props}) => {
    return <Menu.SubMenu title={groupDef.replaceAll(/\_/g, ' ')} key={`t${idx}`} {...props}>
        <TreeChild key={key + '!'}/>
    </Menu.SubMenu>
}

const MenuWebDashboards = ({key, ...props}) => {
    const [data] = useGetOnceHttp({url: '/api/wd/all', def: []})
    const items = data.filter(i => i.path)

    return <TreeRender
        MainComponent={SubMenu}
        LeafComponent={({cd, item, key, subMenuKey, eventKey, ...props}) =>
            <Menu.Item key={item.id} {...props}><Link to={`/wd/${item.id}`}>
                <Tooltip title={item.hint}>{item.title}</Tooltip>
            </Link></Menu.Item>}
        BranchComponent={Branch}
        collapser={(data) => (data)}
        data={items}
        key="SubMenu" icon={<AreaChartOutlined/>} title="Дэшборды"
        splitter={'/'}
        {...props}
        id={"wd-x"}
    />;
}


const LeafMenuNsi = ({cd, item, key, subMenuKey, eventKey, ...props}) =>
    <Menu.Item key={item.oid} {...props}><Link to={`/nsi/${item.oid}`}>
        <Tooltip title={item.caption}>{item.caption}</Tooltip>
    </Link></Menu.Item>

const MenuNsi = ({key, ...props}) => {
    const history = useHistory()
    const navigateTo = (path) => () => history.push(path)
    const [data] = useGetOnceHttp({url: '/api/nsi/all', method: 'POST', def: []})
    const dataReady = data.map(i => ({...i, path: '', title: i.caption})).sort((a, b) => (a.oid > b.oid? 1 : a.oid == b.oid ? 0: -1))
    return <SubMenu {...props} key={"nsi"} onTitleClick={navigateTo('/nsi')} title={"НСИ"}>
        {dataReady.map((item, i) => <LeafMenuNsi key={item.oid || i} item={item}/>)}
    </SubMenu>
}


const BrandLink = () => <Brand to={"/"}><Mark>DT</Mark>Digital Twin</Brand>

const modelItems = () => modelList.map(({key, name}) => (
    <Menu.Item key={`/models/${key}`}>
        <Link to={`/models/${key}`}>{name}</Link>
    </Menu.Item>
))

// На узком экране горизонтальное меню antd нечитаемо: пункты сжимаются
// в многоточие. Показываем марку и бургер, а навигацию уводим в Drawer.
const MobileHeader = ({location, style}) => {
    const {user, logout} = useContext(AuthContext)
    const [open, setOpen] = useState(false)
    const close = () => setOpen(false)

    return <>
        <StyledHeader style={style}>
            <Bar>
                <BrandLink/>
                <Button type={"text"} aria-label={"Меню"} onClick={() => setOpen(true)}
                        icon={<MenuOutlined style={{fontSize: 18}}/>}/>
            </Bar>
        </StyledHeader>
        <Drawer title={"Меню"} placement={"right"} width={288} visible={open} onClose={close}
                bodyStyle={{padding: 0}}>
            <Menu mode={"inline"} selectedKeys={[location.pathname]} onClick={close}
                  style={{borderRight: 0}}>
                <Menu.ItemGroup title={"Модели"}>
                    {modelItems()}
                </Menu.ItemGroup>
                <Menu.Divider/>
                <Menu.Item key={"sett"} icon={<SettingOutlined/>}>
                    <Link to={"/userprofile"}>Настройки{user?.username ? `: ${user.username}` : ''}</Link>
                </Menu.Item>
                <Menu.Item key={"out:1"} icon={<LogoutOutlined/>} onClick={() => {close(); logout()}}>
                    Выход
                </Menu.Item>
            </Menu>
        </Drawer>
    </>
}

const DesktopHeader = ({location, style}) => {
    const {user, logout} = useContext(AuthContext)

    const userMenu = (
        <Menu>
            <Menu.Item key={"sett"} icon={<SettingOutlined/>}>
                <Link to={"/userprofile"}>Настройки пользователя</Link>
            </Menu.Item>
            <Menu.Divider/>
            <Menu.Item key={"out"} icon={<LogoutOutlined/>} onClick={() => logout()}>Выход</Menu.Item>
        </Menu>
    )

    return <StyledHeader style={style}>
        <Bar>
            <div style={{display: 'flex', alignItems: 'center', gap: 24, minWidth: 0}}>
                <BrandLink/>
                <StyledMenu mode="horizontal" selectedKeys={[location.pathname]}
                            style={{borderBottom: 'none', minWidth: 0, flex: 'auto'}}>
                    <SubMenu key="/models" icon={<FontAwesomeIcon icon={faFileChartLine}/>} title={"Модели"}>
                        {modelItems()}
                    </SubMenu>
                    {/*
                    
                                <MenuItemStyled key="/patients" icon={<FontAwesomeIcon icon={faUserFriends}/>}>
                                    <Link to="/patients">Регистр</Link>
                                </MenuItemStyled>
                                <SubMenu key="/reports" icon={<FontAwesomeIcon icon={faFileChartLine}/>} title={"  Отчёты            "}>
                                    <SubMenu key="/reports/f" icon={<OrderedListOutlined/>} title="Отчётные формы">
                                        <Menu.Item key="/reports/f7"><Link to="/admin/users">Форма №7 ФСН</Link></Menu.Item>
                                        <Menu.Item key="/reports/f12"><Link to="/admin/users">Форма №12 ФСН</Link></Menu.Item>
                                        <Menu.Item key="/reports/f14"><Link to="/admin/users">Форма №14 ФСН</Link></Menu.Item>
                                        <Menu.Item key="/reports/fBOZ"><Link to="/admin/users">Форма АСММС.БОЗ</Link></Menu.Item>
                                        <Menu.Item key="/reports/fMSS"><Link to="/admin/users">Форма АСММС.МСС</Link></Menu.Item>
                                        <Menu.Item key="/reports/fCAOP"><Link to="/admin/users">Форма АСММС.Мониторинг ЦАОП</Link></Menu.Item>
                                        <Menu.Item key="/reports/fCAOPD"><Link to="/admin/users">Форма АСММС.Мониторинг деятельности
                                            ЦАОП</Link></Menu.Item>
                                        <Menu.Item key="/reports/users"><Link to="/admin/users">Форма ЦИТИС</Link></Menu.Item>
                                        <Menu.Item key="/reports/users"><Link to="/admin/users">Форма Электронный бюджет</Link></Menu.Item>
                                    </SubMenu>
                                    <SubMenu key="/reports/g1" icon={<OrderedListOutlined/>} title="Заболеваемость"/>
                                    <SubMenu key="/reports/g2" icon={<OrderedListOutlined/>} title="Смертность"/>
                                    <SubMenu key="/reports/g3" icon={<OrderedListOutlined/>} title="Показатели службы"/>
                                    <SubMenu key="/reports/g4" icon={<OrderedListOutlined/>} title="Лечение"/>
                    
                                    <SubMenu key="/reports/g5" icon={<OrderedListOutlined/>} title="Аналитические отчёты"/>
                                    <SubMenu key="/reports/g6" icon={<OrderedListOutlined/>} title="KPI"/>
                                    <SubMenu key="/reports/g7" icon={<OrderedListOutlined/>} title="Сроки маршрутизации"/>
                                    <SubMenu key="/reports/g8" icon={<OrderedListOutlined/>} title="Лечение"/>
                    
                                    <SubMenu key="/reports/g9" icon={<OrderedListOutlined/>} title="Критерии качество оказания помощи"/>
                    
                                    <SubMenu key="/reports/g10" icon={<OrderedListOutlined/>} title="Сводные отчёты"/>
                                    <SubMenu key="/reports/g11" icon={<OrderedListOutlined/>} title="Дополнительные сведения"/>
                                    <SubMenu key="/reports/g12" icon={<OrderedListOutlined/>} title="Прогноз потребности"/>
                                    <MenuItemStyled key="/reports/add" icon={<PlusOutlined/>} title="Новая группа отчётов">Новая группа
                                        отчётов</MenuItemStyled>
                                </SubMenu>
                                <SubMenu key="Admin" icon={<SettingOutlined/>} title="Администрирование">
                                    <Menu.Item key="/admin/users"><Link to="/admin/users">Управление пользователями</Link></Menu.Item>
                                    <Menu.Item key="/admin/roles"><Link to="/admin/roles">Управление ролями</Link></Menu.Item>
                                    <Menu.Item key="/admin/system"> <Link to="/admin/system">Клинические рекомендации</Link></Menu.Item>
                                    <Menu.Item key="/admin/routing"><Link to="/admin/routing">Управление маршрутизацией</Link></Menu.Item>
                                    <Menu.Item key="/admin/triggers"><Link to="/admin/triggers">Управление триггерами</Link></Menu.Item>
                                    <Menu.Item key="/admin/flk"><Link to="/admin/flk">Управление внешними ФЛК</Link></Menu.Item>
                                    <Menu.Item key="/admin/ctrl"><Link to="/admin/ctrl">Управление правилами отбора
                                        данных</Link></Menu.Item>
                                    <Menu.Item key="/admin/nsi"><Link to="/admin/nsi">Управление НСИ</Link></Menu.Item>
                                    <Menu.Item key="/admin/wd"><Link to="/admin/wd">Управление дэшбордами</Link></Menu.Item>
                                    <Menu.Item key="/admin"><Link to="/admin">Админ</Link></Menu.Item>
                                </SubMenu>
                                <MenuWebDashboards key={"db"}/>
                                <MenuNsi/>*/}
                </StyledMenu>
            </div>

            <Dropdown overlay={userMenu} trigger={["click"]} placement={"bottomRight"}>
                <Button type={"text"} style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <UserOutlined/>
                    <span style={{maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {user?.username || 'Пользователь'}
                    </span>
                </Button>
            </Dropdown>
        </Bar>
    </StyledHeader>
}

const Header = ({location, style}) => {
    const isMobile = useIsMobile()

    if (hideHeader(location)) return <></>
    return isMobile
        ? <MobileHeader location={location} style={style}/>
        : <DesktopHeader location={location} style={style}/>
}

export default Header;
